import { useRef } from 'react';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@savvy-kit/components/empty';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { DrawIn, useFadeOnChange, useMorphSeries } from '@savvy-kit/foundations/motion/chart-motion';
import { DELAY_CURVE } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { ChartContainer, ChartTooltip } from '@savvy-kit/patterns/chart';
import { isNil } from 'es-toolkit';
import { Activity } from 'lucide-react';
import { DateTime } from 'luxon';
import { Area, AreaChart, XAxis, YAxis } from 'recharts';

import type { HomeSeriesPoint } from './types';
import { CLIENT_PORTAL_CHART_AXIS_DIM_CLASS, CLIENT_PORTAL_CHART_AXIS_FADE_CLASS } from '../common/constants';
import { chartSpansMultipleYears, formatChartTickDate, safeLargeMoney, safeMoney } from '../common/utils';
import {
  HOME_CHART_CONFIG,
  HOME_CHART_GRADIENT_ID,
  HOME_CHART_NO_HISTORY_MESSAGE,
  HOME_CHART_NO_HISTORY_TITLE,
  HOME_CHART_SINGLE_READING_MESSAGE,
  HOME_CHART_X_TICK_MIN_GAP,
  HOME_CHART_Y_AXIS_WIDTH,
  HOME_CHART_Y_TICK_COUNT,
} from './constants';

type HomeValueChartProps = {
  readonly series: readonly HomeSeriesPoint[];
  readonly loading: boolean;
  readonly ariaLabel: string;
  /** Added to the curve's beat so it draws after a page-level reveal fade rather than under it. */
  readonly delayOffset?: number;
};

type ChartTooltipProps = {
  readonly active?: boolean;
  readonly payload?: readonly { readonly payload?: HomeSeriesPoint }[];
};

function formatPointDate(isoDate: string) {
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('LLLL d') : isoDate;
}

function HomeValueTooltip({ active, payload }: ChartTooltipProps) {
  const point = payload?.[0]?.payload;
  if (active !== true || isNil(point)) {
    return null;
  }

  const legs = [
    { key: 'managed', label: HOME_CHART_CONFIG.managed.label, value: point.managed },
    { key: 'heldAway', label: HOME_CHART_CONFIG.heldAway.label, value: point.heldAway },
  ].filter((leg) => !isNil(leg.value));

  return (
    <div className="max-w-[calc(100vw-7rem)] rounded-lg border border-border-light bg-popover px-4 py-3 shadow-md md:min-w-52">
      <p className="text-sm text-secondary">{formatPointDate(point.date)}</p>
      <Text variant="h3" render={<p />} className="tabular-nums">
        {safeMoney(point.total)}
      </Text>
      {legs.length > 1 && (
        <div className="mt-3 flex flex-col gap-1.5 border-t border-border-light pt-3">
          {legs.map((leg) => (
            <div key={leg.key} className="flex items-center justify-between gap-4 text-sm">
              <span className="flex items-center gap-2 text-secondary">
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: `var(--color-${leg.key})` }} />
                {leg.label}
              </span>
              <span className="font-medium tabular-nums text-dark">{safeMoney(leg.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function HomeValueChart({ series, loading, ariaLabel, delayOffset = 0 }: HomeValueChartProps) {
  // A background refetch flips `loading` back to true with the curve still on screen. Swapping in a skeleton
  // there unmounts the morph and the wipe mid-flight, so the whole draw replays on every refetch; hold the
  // last good series and dim it in place instead.
  const heldRef = useRef<readonly HomeSeriesPoint[]>([]);
  if (!loading && series.length > 0) {
    heldRef.current = series;
  }
  const shown = loading ? heldRef.current : series;

  const frame = useMorphSeries(shown, ['total']);
  const axisDimmed = useFadeOnChange(shown);

  if (loading && shown.length === 0) {
    return <Skeleton className="mx-4 h-64" />;
  }

  if (shown.length === 0) {
    return (
      <Empty className="h-64 gap-4">
        <EmptyHeader>
          <EmptyMedia variant="icon" className="border-border-light bg-vanilla text-secondary">
            <Activity />
          </EmptyMedia>
          <EmptyTitle>{HOME_CHART_NO_HISTORY_TITLE}</EmptyTitle>
          <EmptyDescription>{HOME_CHART_NO_HISTORY_MESSAGE}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  if (shown.length < 2) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-secondary">{HOME_CHART_SINGLE_READING_MESSAGE}</p>
      </div>
    );
  }

  const withYear = chartSpansMultipleYears(shown);

  return (
    <DrawIn delay={DELAY_CURVE + delayOffset}>
      <ChartContainer
        config={HOME_CHART_CONFIG}
        role="img"
        aria-label={ariaLabel}
        className={cn(
          'aspect-auto h-64 w-full touch-pan-y transition-opacity duration-200',
          CLIENT_PORTAL_CHART_AXIS_FADE_CLASS,
          axisDimmed && CLIENT_PORTAL_CHART_AXIS_DIM_CLASS,
          loading && 'opacity-50',
        )}
      >
        <AreaChart data={[...frame]} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={HOME_CHART_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-total)" stopOpacity={0.28} />
              <stop offset="100%" stopColor="var(--color-total)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: 'var(--secondary)' }}
            tickLine={false}
            axisLine={false}
            tickMargin={12}
            interval="preserveStartEnd"
            minTickGap={HOME_CHART_X_TICK_MIN_GAP}
            tickFormatter={(isoDate: string) => formatChartTickDate(isoDate, withYear)}
          />
          <YAxis
            dataKey="total"
            domain={['auto', 'auto']}
            tick={{ fontSize: 12, fill: 'var(--secondary)' }}
            tickLine={false}
            axisLine={false}
            tickCount={HOME_CHART_Y_TICK_COUNT}
            width={HOME_CHART_Y_AXIS_WIDTH}
            tickFormatter={(value: number) => safeLargeMoney(value)}
          />
          <ChartTooltip content={<HomeValueTooltip />} />
          <Area
            dataKey="total"
            type="monotone"
            stroke="var(--color-total)"
            baseValue="dataMin"
            strokeWidth={1.5}
            fill={`url(#${HOME_CHART_GRADIENT_ID})`}
            fillOpacity={1}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ChartContainer>
    </DrawIn>
  );
}
